import arrowIosBackFill from '@iconify/icons-eva/arrow-ios-back-fill';
import { Icon } from '@iconify/react';
import { Alert, Box, Button, Card, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import MHidden from 'components/@material-extend/MHidden/MHidden';
import NewPasswordForm from 'components/auth/ResetPassword/NewPasswordForm';
import Page from 'components/Page/Page';
import { checkPasswordResetCode, handleVerifyEmail, refreshToken } from 'external/firebase/controllers/auth';
import useLocales from 'hooks/useLocales';
import AuthLayout from 'layouts/AuthLayout';
import { PATH_AUTH } from 'routes/paths';

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        display: 'flex',
    },
}));

const SectionStyle = styled(Card)(({ theme }) => ({
    width: '100%',
    maxWidth: 464,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: theme.spacing(2, 0, 2, 2),
}));

const ContentStyle = styled('div')(({ theme }) => ({
    maxWidth: 480,
    margin: 'auto',
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

const UserManagement: React.FunctionComponent = () => {
    const { search } = useLocation();
    const { translate } = useLocales();
    const searchParams = new URLSearchParams(search);

    const [error, setError] = useState<string | undefined>();

    const mode = searchParams.get('mode') || '';
    const actionCode = searchParams.get('oobCode') || '';

    const handleUserManagement = async (mode: string, actionCode: string) => {
        switch (mode) {
            case 'resetPassword':
                // eslint-disable-next-line no-case-declarations
                const checkPasswordResetCodeResponse = await checkPasswordResetCode(actionCode);
                if (!checkPasswordResetCodeResponse.ok) {
                    setError(translate(checkPasswordResetCodeResponse.code));
                }
                break;
            case 'verifyEmail':
                // eslint-disable-next-line no-case-declarations
                const handleVerifyEmailResponse = await handleVerifyEmail(actionCode);
                if (!handleVerifyEmailResponse.ok) {
                    setError(translate(handleVerifyEmailResponse.code));
                    break;
                }
                await refreshToken();
                window.location.replace(PATH_AUTH.verify);
                break;
            default:
                setError(translate('incorrect_or_tampered_link'));
        }
    };

    useEffect(() => {
        handleUserManagement(mode, actionCode);
    }, [mode, actionCode]);

    const displayUserManagementComponent = (mode: string, actionCode: string) => {
        switch (mode) {
            case 'resetPassword':
                return (
                    <>
                        <Button
                            size='small'
                            color='secondary'
                            component={RouterLink}
                            to={PATH_AUTH.resetPassword}
                            startIcon={<Icon icon={arrowIosBackFill} width={20} height={20} />}
                            sx={{ mb: 3, textTransform: 'capitalize' }}
                        >
                            {translate('back')}
                        </Button>
                        <Typography variant='h3' paragraph>
                            {translate('enter_new_password_title')}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', mb: 5 }}>
                            {translate('enter_new_password_subtitle')}
                        </Typography>

                        <NewPasswordForm actionCode={actionCode} />
                    </>
                );
            case 'verifyEmail':
                return `${translate('redirecting')}...`;
            default:
                return translate('incorrect_or_tampered_link');
        }
    };

    return (
        <RootStyle title={`${translate('user_management')} | CodeAcademy Kids`}>
            <AuthLayout />

            <MHidden width='mdDown'>
                <SectionStyle />
            </MHidden>

            <Container>
                <ContentStyle>
                    <Box sx={{ maxWidth: 480, mx: 'auto' }}>
                        {error ? (
                            <Alert severity='error'>{error}</Alert>
                        ) : (
                            displayUserManagementComponent(mode, actionCode)
                        )}
                    </Box>
                </ContentStyle>
            </Container>
        </RootStyle>
    );
};

export default UserManagement;
